export type Theme = 'light' | 'dark'

/** Where the choice is remembered between visits. */
export const THEME_STORAGE_KEY = 'augrank.theme'

/**
 * Reads the stored preference, falling back to the operating system.
 *
 * Its own module rather than a second export from the component: "light and
 * dark both work, everywhere" (CLAUDE.md, Frontend rules) is a property of this
 * function more than of the button, and it is testable without rendering
 * anything.
 */
export function preferredTheme(storage: Pick<Storage, 'getItem'>, matchesDark: boolean): Theme {
  const stored = storage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return matchesDark ? 'dark' : 'light'
}

/** The stored choice, or null when the person has never pressed the toggle. */
export function storedTheme(storage: Pick<Storage, 'getItem'>): Theme | null {
  const stored = storage.getItem(THEME_STORAGE_KEY)
  return stored === 'light' || stored === 'dark' ? stored : null
}

/** The token layer keys every colour off this one attribute on the root. */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
}

/**
 * Applies the theme once at boot and keeps following the operating system
 * until the person makes a choice of their own.
 *
 * Before 2026-09-12 nothing did this: the attribute was set only by a mounted
 * `ThemeToggle`, in an effect that also *wrote the resolved value back to
 * storage on every mount*. Two things followed. A public page pinned the theme
 * to whatever the OS was on the first visit and never followed it again — a
 * laptop that goes dark at sunset stayed light. And the shell, where the toggle
 * sits inside the closed account menu, set no attribute at all: it showed the
 * OS theme regardless of the stored choice until the menu was opened, at which
 * point the effect ran and the whole screen flipped. Found by the screen audit,
 * whose dark screenshots of the sign-in page came out light.
 *
 * Storage is written in exactly one place now — the toggle's click — and the
 * media query is listened to for as long as nothing is stored.
 */
export function bootTheme(storage: Pick<Storage, 'getItem'>, media: MediaQueryList): void {
  applyTheme(preferredTheme(storage, media.matches))
  media.addEventListener('change', (event) => {
    if (storedTheme(storage) === null) applyTheme(event.matches ? 'dark' : 'light')
  })
}
